/**
 * FormRenderer — THE question renderer shared by every CRM form surface:
 * prescreen previews + the in-call prescreen runner (Phase B), the form
 * builder preview and public texted forms (Phase C).
 *
 * One schema, one renderer: questions are { key, label, type, options }, with
 * answers held by the caller as a key->value map. mode 'page' renders every
 * question in a column; mode 'single' renders exactly one question with large
 * touch targets for live calls / phones.
 *
 * Two type vocabularies coexist: the prescreen set (boolean | number | choice
 * | text | date) and the WCR-derived forms set (yes_no | single_choice |
 * multiple_choice | scale | height | textarea | email | phone). Value shapes
 * follow the source conventions so answers stay comparable across systems —
 * notably yes_no emits the STRINGS 'yes'/'no' (not booleans) and height emits
 * total inches as a string, exactly like the old WCR site.
 */
import { useState } from 'react';

export interface FormQuestion {
  key: string;
  label: string;
  /** prescreen: boolean|number|choice|text|date · forms: yes_no|single_choice|multiple_choice|scale|height|textarea|email|phone. Unknowns render as text. */
  type: string;
  options?: { value: string; label: string }[] | null;
  required?: boolean;
  helpText?: string | null;
  placeholder?: string | null;
  min?: number | null;
  max?: number | null;
}

export type FormAnswers = Record<string, any>;

/** Soft keyboard action hint for text-like inputs (mobile best practice). */
export type EnterKeyHint = 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';

interface FormRendererProps {
  questions: FormQuestion[];
  answers: FormAnswers;
  onAnswer: (key: string, value: any) => void;
  mode?: 'page' | 'single';
  readOnly?: boolean;
  /** page mode only: per-question validation messages (key -> message), shown
   * under the offending input. Undefined = nothing rendered (no behavior change
   * for existing callers). */
  errors?: Record<string, string>;
  /** Soft-keyboard hint applied to text-like inputs; e.g. 'next' so a phone
   * keyboard shows a "next" key for a multi-question page. */
  enterKeyHint?: EnterKeyHint;
  /** Fires when a single tap fully COMMITS an answer (yes/no, single choice,
   * scale button, the second half of a height pick) — i.e. the moments a page
   * can safely advance. Never fires for typed inputs (they commit per
   * keystroke) or multiple_choice (select-all-that-apply has no "done" tap). */
  onChoiceCommit?: (key: string) => void;
}

/** Selected must read as CHOSEN, not merely highlighted: a solid fill (vs. the
 * faint hover tint) plus an explicit checkmark. Hover styles are gated to
 * hover-capable devices — on touch screens a tap otherwise leaves a stuck
 * hover tint that looks like a selection. */
const choiceBtnCls = (selected: boolean) =>
  `flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-left text-sm transition-all duration-150 min-h-[44px] sm:min-h-0 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-800 ${
    selected
      ? 'border-primary-600 bg-primary-600 font-semibold text-white dark:border-primary-500 dark:bg-primary-600'
      : 'border-gray-300 bg-white font-medium text-gray-700 [@media(hover:hover)]:hover:border-primary-400 [@media(hover:hover)]:hover:bg-primary-50/50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:[@media(hover:hover)]:hover:border-primary-600'
  }`;

/** Always rendered (opacity-toggled) so the button width never jumps when a
 * selection lands. */
const CheckMark = ({ selected }: { selected: boolean }) => (
  <svg
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    className={`h-5 w-5 shrink-0 transition-opacity duration-150 ${selected ? 'opacity-100' : 'opacity-0'}`}
  >
    <path fillRule="evenodd" clipRule="evenodd" d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.09 7.153a1 1 0 0 1-1.42 0L4.29 9.93a1 1 0 1 1 1.42-1.408l1.2 1.21 6.38-6.437a1 1 0 0 1 1.414-.006z" />
  </svg>
);

// min-h-[44px] sm:min-h-0 gives every text-like control (and the ft/in selects)
// the same 44px mobile touch-target floor as the choice buttons above — these
// are the bulk of a phone-filled questionnaire. text-base sm:text-sm keeps the
// font at 16px on phones: iOS Safari auto-zooms the page when a focused
// input/select is under 16px, and never zooms back out (dobSelectCls pattern).
// NOTE inputCls bakes in w-full — appending `w-auto` does NOT reliably override
// it (equal specificity; the generated stylesheet order wins, and w-full wins
// there). Composites that need narrower controls must use grid columns, not
// width overrides.
const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-base sm:text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 min-h-[44px] sm:min-h-0 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

/** ft/in composite for the WCR `height` type — value is total inches as a
 * string. "Feet chosen / inches unset" is unrepresentable in total inches, so
 * the picks live in local state and the value only commits once BOTH selects
 * are set (WCR's HeightQuestion behavior) — otherwise picking just feet would
 * silently record N feet 0 inches and satisfy a required question. */
function HeightInput({ value, onChange, onCommitted, disabled }: { value: any; onChange: (v: any) => void; onCommitted?: () => void; disabled: boolean }) {
  const total = Number(value);
  const committed = Number.isFinite(total) && total > 0;
  const [feet, setFeet] = useState<string>(() => (committed ? String(Math.floor(total / 12)) : ''));
  const [inches, setInches] = useState<string>(() => (committed ? String(total % 12) : ''));
  const commit = (f: string, i: string) => {
    if (f === '' || i === '') {
      onChange(null);
      return;
    }
    onChange(String(Number(f) * 12 + Number(i)));
    onCommitted?.();
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      <select
        disabled={disabled}
        value={feet}
        onChange={(e) => { setFeet(e.target.value); commit(e.target.value, inches); }}
        className={`${inputCls} min-w-0`}
        aria-label="Feet"
      >
        <option value="">ft</option>
        {[3, 4, 5, 6, 7].map((f) => (
          <option key={f} value={f}>{f} ft</option>
        ))}
      </select>
      <select
        disabled={disabled || feet === ''}
        value={inches}
        onChange={(e) => { setInches(e.target.value); commit(feet, e.target.value); }}
        className={`${inputCls} min-w-0`}
        aria-label="Inches"
      >
        <option value="">in</option>
        {Array.from({ length: 12 }, (_, i) => (
          <option key={i} value={String(i)}>{i} in</option>
        ))}
      </select>
    </div>
  );
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Month/Day/Year select composite for the `date` type (birth dates above all).
 * Three native <select>s REPLACE <input type="date"> so the OS date sheet never
 * auto-opens the instant the field is tapped/focused — a patient who taps just
 * to read is otherwise buried under a full-screen calendar before they can see
 * WHICH date is being asked for. A native select opens its short wheel only on
 * an explicit tap of that dropdown, and the question label stays in view behind
 * it. Value commits as an ISO `YYYY-MM-DD` string (exactly what the age
 * calculation + the backend expect) and ONLY once all three parts are set — a
 * partial pick emits null so a required DOB isn't satisfied early (HeightInput
 * pattern). The stored shape is byte-for-byte the same as the old native input,
 * so age/eligibility/show_if downstream are untouched. */
function DateInput({ value, onChange, onCommitted, disabled }: { value: any; onChange: (v: any) => void; onCommitted?: () => void; disabled: boolean }) {
  // Split the ISO string rather than new Date() to parse — new Date('YYYY-MM-DD')
  // is UTC-midnight and would shift back a day when rendered in a negative-offset
  // zone. month/day are held WITHOUT leading zeros (1–12, 1–31) to match option values.
  const parsed = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  const [year, setYear] = useState<string>(() => (parsed ? parsed[1] : ''));
  const [month, setMonth] = useState<string>(() => (parsed ? String(Number(parsed[2])) : ''));
  const [day, setDay] = useState<string>(() => (parsed ? String(Number(parsed[3])) : ''));

  const currentYear = new Date().getFullYear();
  // Descending (this year first, 1900 floor) — recent years sit at the top and
  // 1900 covers any living date of birth.
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);
  // Clamp the day list to the picked month/year so Feb never offers the 30th
  // (day 0 of the next month = last day of this one; year defaults keep leap
  // years honest). With no month picked yet, offer the full 31.
  const daysInMonth = month ? new Date(Number(year || currentYear), Number(month), 0).getDate() : 31;

  const commit = (mo: string, d: string, y: string) => {
    if (mo === '' || d === '' || y === '') { onChange(null); return; }
    onChange(`${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`);
    onCommitted?.();
  };
  // Changing month or year can strand a previously-picked day (31 → Feb, or
  // Feb 29 → a non-leap year): drop it so we never store an impossible date.
  const clampDay = (mo: string, y: string): string => {
    if (!mo || !day) return day;
    const max = new Date(Number(y || currentYear), Number(mo), 0).getDate();
    return Number(day) > max ? '' : day;
  };

  // Grid, not flex + width overrides: `${inputCls} w-auto` loses to inputCls's
  // baked-in w-full (stylesheet order), which blew Day/Year up to full width
  // and crushed the Month select to a sliver on phones. Fixed fr columns with
  // min-w-0 items size deterministically everywhere (PublicSign DobWheels
  // pattern); Month gets the wide column since "September" is the longest label.
  return (
    <div className="grid grid-cols-[1.5fr_1fr_1fr] gap-2">
      <select
        disabled={disabled}
        value={month}
        onChange={(e) => { const mo = e.target.value; setMonth(mo); const d = clampDay(mo, year); if (d !== day) setDay(d); commit(mo, d, year); }}
        className={`${inputCls} min-w-0`}
        aria-label="Month"
      >
        <option value="">Month</option>
        {MONTHS.map((name, i) => (
          <option key={i} value={i + 1}>{name}</option>
        ))}
      </select>
      <select
        disabled={disabled}
        value={day}
        onChange={(e) => { setDay(e.target.value); commit(month, e.target.value, year); }}
        className={`${inputCls} min-w-0`}
        aria-label="Day"
      >
        <option value="">Day</option>
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
      <select
        disabled={disabled}
        value={year}
        onChange={(e) => { const y = e.target.value; setYear(y); const d = clampDay(month, y); if (d !== day) setDay(d); commit(month, d, y); }}
        className={`${inputCls} min-w-0`}
        aria-label="Year"
      >
        <option value="">Year</option>
        {years.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
}

function QuestionInput({
  q,
  value,
  onChange,
  onCommitted,
  readOnly,
  large,
  enterKeyHint,
}: {
  q: FormQuestion;
  value: any;
  onChange: (v: any) => void;
  /** See FormRendererProps.onChoiceCommit — tap-commit moments only. */
  onCommitted?: () => void;
  readOnly?: boolean;
  large?: boolean;
  enterKeyHint?: EnterKeyHint;
}) {
  const disabled = Boolean(readOnly);
  switch (q.type) {
    case 'boolean':
      return (
        <div className={`grid gap-2 ${large ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2'}`}>
          {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(({ v, l }) => (
            <button
              key={l}
              type="button"
              disabled={disabled}
              aria-pressed={value === v}
              onClick={() => { onChange(v); onCommitted?.(); }}
              className={choiceBtnCls(value === v)}
            >
              <span>{l}</span>
              <CheckMark selected={value === v} />
            </button>
          ))}
        </div>
      );
    case 'yes_no':
      // WCR convention: the stored value is the STRING 'yes' / 'no'.
      return (
        <div className={`grid gap-2 ${large ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2'}`}>
          {[{ v: 'yes', l: 'Yes' }, { v: 'no', l: 'No' }].map(({ v, l }) => (
            <button
              key={v}
              type="button"
              disabled={disabled}
              aria-pressed={value === v}
              onClick={() => { onChange(v); onCommitted?.(); }}
              className={choiceBtnCls(value === v)}
            >
              <span>{l}</span>
              <CheckMark selected={value === v} />
            </button>
          ))}
        </div>
      );
    case 'choice':
    case 'single_choice':
      return (
        <div className="grid gap-2">
          {/* Index in the key: imported WCR options may carry duplicate values. */}
          {(q.options || []).map((o, i) => (
            <button
              key={`${i}:${o.value}`}
              type="button"
              disabled={disabled}
              aria-pressed={value === o.value}
              onClick={() => { onChange(o.value); onCommitted?.(); }}
              className={choiceBtnCls(value === o.value)}
            >
              <span>{o.label}</span>
              <CheckMark selected={value === o.value} />
            </button>
          ))}
        </div>
      );
    case 'multiple_choice': {
      const selected: string[] = Array.isArray(value) ? value : [];
      const toggle = (v: string) => {
        const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
        onChange(next.length > 0 ? next : null);
      };
      return (
        <div className="grid gap-2">
          {(q.options || []).map((o, i) => (
            <button
              key={`${i}:${o.value}`}
              type="button"
              disabled={disabled}
              aria-pressed={selected.includes(o.value)}
              onClick={() => toggle(o.value)}
              className={choiceBtnCls(selected.includes(o.value))}
            >
              <span>{o.label}</span>
              <CheckMark selected={selected.includes(o.value)} />
            </button>
          ))}
          <p className="text-xs text-gray-400 dark:text-gray-500">Select all that apply</p>
        </div>
      );
    }
    case 'scale': {
      // Fallback range mirrors WCR's ScaleQuestion (min ?? 1, max ?? 10);
      // builder-minted and imported scales carry explicit min/max anyway.
      const min = Number.isFinite(Number(q.min)) && q.min != null ? Number(q.min) : 1;
      const max = Number.isFinite(Number(q.max)) && q.max != null ? Number(q.max) : 10;
      const span = max - min;
      if (span < 1 || span > 14) {
        // Degenerate or oversized range — fall back to a plain number input.
        return (
          <input
            type="number"
            inputMode="numeric"
            disabled={disabled}
            min={min}
            max={max}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            className={inputCls}
          />
        );
      }
      return (
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: span + 1 }, (_, i) => min + i).map((n) => {
            const isSel = Number(value) === n && value !== null && value !== undefined && value !== '';
            return (
              <button
                key={n}
                type="button"
                disabled={disabled}
                aria-pressed={isSel}
                onClick={() => { onChange(n); onCommitted?.(); }}
                className={`flex h-11 w-11 items-center justify-center rounded-lg border text-sm transition-all duration-150 active:scale-95 sm:h-9 sm:w-9 ${
                  isSel
                    ? 'border-primary-600 bg-primary-600 font-semibold text-white'
                    : 'border-gray-300 bg-white font-medium text-gray-700 [@media(hover:hover)]:hover:border-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                }`}
              >
                {n}
              </button>
            );
          })}
        </div>
      );
    }
    case 'height':
      // Keyed by question: HeightInput holds local ft/in state, and the
      // single-question flow reuses this component position across questions.
      return <HeightInput key={q.key} value={value} onChange={onChange} onCommitted={onCommitted} disabled={disabled} />;
    case 'number':
      return (
        <input
          type="number"
          inputMode="decimal"
          enterKeyHint={enterKeyHint}
          disabled={disabled}
          min={q.min ?? undefined}
          max={q.max ?? undefined}
          placeholder={q.placeholder || undefined}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className={inputCls}
        />
      );
    case 'date':
      // Keyed by question (like height): DateInput holds local M/D/Y state, and
      // the single-question flow reuses this component position across questions.
      return <DateInput key={q.key} value={value} onChange={onChange} onCommitted={onCommitted} disabled={disabled} />;
    case 'email':
      return (
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          enterKeyHint={enterKeyHint}
          disabled={disabled}
          placeholder={q.placeholder || undefined}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={inputCls}
        />
      );
    case 'phone':
      return (
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          enterKeyHint={enterKeyHint}
          disabled={disabled}
          placeholder={q.placeholder || '(555) 555-5555'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={inputCls}
        />
      );
    case 'text':
      return (
        <input
          type="text"
          enterKeyHint={enterKeyHint}
          disabled={disabled}
          placeholder={q.placeholder || undefined}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={inputCls}
        />
      );
    case 'textarea':
      return (
        <textarea
          rows={3}
          disabled={disabled}
          placeholder={q.placeholder || undefined}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={inputCls}
        />
      );
    case 'calculation':
      // Derived value — never directly answered. Shown read-only as a safety net
      // (callers normally render calc rows with their own computed display).
      return (
        <div className={`${inputCls} bg-gray-50 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300`}>
          {value === null || value === undefined || value === '' ? '—' : String(value)}
        </div>
      );
    default:
      return (
        <textarea
          rows={2}
          disabled={disabled}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={inputCls}
        />
      );
  }
}

export default function FormRenderer({ questions, answers, onAnswer, mode = 'page', readOnly, errors, enterKeyHint, onChoiceCommit }: FormRendererProps) {
  if (mode === 'single') {
    const q = questions[0];
    if (!q) return null;
    return (
      <div>
        <div className="mb-3 text-base font-medium text-gray-900 dark:text-gray-100">
          {q.label}
          {q.required && <span className="ml-1 text-red-500">*</span>}
        </div>
        {q.helpText && <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">{q.helpText}</p>}
        <QuestionInput q={q} value={answers[q.key]} onChange={(v) => onAnswer(q.key, v)} onCommitted={onChoiceCommit ? () => onChoiceCommit(q.key) : undefined} readOnly={readOnly} enterKeyHint={enterKeyHint} large />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {questions.map((q, i) => {
        const err = errors?.[q.key];
        return (
          // data-question-key lets a parent locate this question's block for
          // focus-next-on-Enter and scroll-to-first-error (QuestionnaireForm).
          <div key={q.key} data-question-key={q.key} className="scroll-mt-4">
            <div className="mb-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
              <span className="mr-1.5 text-gray-400 dark:text-gray-500">{i + 1}.</span>
              {q.label}
              {q.required && <span className="ml-1 text-red-500">*</span>}
            </div>
            {q.helpText && <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{q.helpText}</p>}
            <QuestionInput q={q} value={answers[q.key]} onChange={(v) => onAnswer(q.key, v)} onCommitted={onChoiceCommit ? () => onChoiceCommit(q.key) : undefined} readOnly={readOnly} enterKeyHint={enterKeyHint} />
            {err && <p className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400">{err}</p>}
          </div>
        );
      })}
    </div>
  );
}
